import { animationFrame$ } from "../../../messages/xr";
import { camera } from "../../rendering/camera";
import { PositionalAudio, MeshBasicMaterial, SphereGeometry, Mesh, Object3D } from "three";
import { mainScene } from "../../rendering/mainScene";
/**
 * TrainingMovementTrackerService
 * - Tracks multiple objects (head, leftController, rightController, ...)
 * - Computes instantaneous speed in units/second
 * - Keeps every tracked frame's speed and timestamp and computes mean/median/max
 * - Records stage changes independently of speed samples for the result timeline
 * - Logs violations (timestamp + speed) when speed > threshold
 *
 * Usage example:
 * const tracker = new TrainingMovementTrackerService({ maxHistory: 500, defaultThreshold: 5 })
 * tracker.addObject('head', () => localPlayerContainer.cameraWorldPosition)
 * tracker.start()
 *
 * Then read stats with tracker.getStats('head') and violations with tracker.getViolations('head')
 */
export class TrainingMovementTrackerService {
    constructor(options) {
        this.tracked = new Map();
        this.running = false;
        this.trackingActive = false;
        this.stageChanges = [];
        this.audioVisualFeedbackEnabled = false;
        this.handleFrame = () => {
            // animationFrame$ callbacks don't provide timestamp; use Date.now()
            const timestamp = Date.now();
            this.tracked.forEach((obj) => {
                try {
                    obj.evaluateSpeedAndUpdateSound(timestamp, this.getActiveStage(), this.trackingActive, this.audioVisualFeedbackEnabled);
                }
                catch (e) {
                    // swallow per-object errors so one failing getter doesn't break others
                    // eslint-disable-next-line no-console
                    console.warn("TrainingMovementTrackerService: error while sampling object", obj.name, e);
                }
            });
        };
        this.defaultThreshold = options === null || options === void 0 ? void 0 : options.defaultThreshold;
    }
    getAudioVisualFeedbackEnabled() {
        return this.audioVisualFeedbackEnabled;
    }
    enableAudioVisualFeedback(enabled) {
        this.audioVisualFeedbackEnabled = enabled;
    }
    setActiveStage(stage) {
        if (stage !== this.activeStage)
            this.stageChanges.push({ timestamp: Date.now(), stage });
        this.activeStage = stage;
    }
    getActiveStage() {
        return this.activeStage;
    }
    start() {
        if (this.running)
            return;
        // subscribe to movement updates (fast, called on movement-related frames)
        animationFrame$.onMovementUpdate$.add(this.handleFrame);
        this.running = true;
    }
    setTrackingEnabled(enabled) {
        this.trackingActive = enabled;
    }
    stop() {
        if (!this.running)
            return;
        animationFrame$.onMovementUpdate$.remove(this.handleFrame);
        this.deactivateTrackedObjects();
        this.getTrackedObjects().forEach(trackedObject => {
            var _a, _b, _c, _d, _e, _f, _g;
            (_b = (_a = trackedObject.visualizer) === null || _a === void 0 ? void 0 : _a.parent) === null || _b === void 0 ? void 0 : _b.remove(trackedObject.visualizer);
            const parent = (_c = trackedObject.visualizer) === null || _c === void 0 ? void 0 : _c.parent;
            if (parent)
                mainScene.remove(parent);
            (_d = trackedObject.visualizer) === null || _d === void 0 ? void 0 : _d.geometry.dispose();
            if (Array.isArray((_e = trackedObject.visualizer) === null || _e === void 0 ? void 0 : _e.material))
                (_f = trackedObject.visualizer) === null || _f === void 0 ? void 0 : _f.material.forEach(m => { m.dispose(); });
            else
                (_g = trackedObject.visualizer) === null || _g === void 0 ? void 0 : _g.material.dispose();
        });
        this.running = false;
    }
    dispose() {
        this.stop();
        this.tracked.clear();
    }
    addObject(name, object, visualize, threshold) {
        var _a;
        object.name = name;
        object.speeds = [];
        object.history = [];
        object.violations = [];
        object.threshold = (_a = threshold !== null && threshold !== void 0 ? threshold : this.defaultThreshold) !== null && _a !== void 0 ? _a : 1;
        object.positionalAudio = new PositionalAudio(camera.audioListener);
        object.positionalAudio.setRefDistance(0.1);
        object.positionalAudio.setMaxDistance(10);
        object.positionalAudio.setRolloffFactor(0.1);
        if (this.getAudioVisualFeedbackEnabled()) {
            // Position of the audio source that keeps in sync with the tracked object
            const sourceNode = new Object3D();
            mainScene.add(sourceNode);
            sourceNode.add(object.positionalAudio);
            if (visualize) {
                // Sphere that visualizes the speed
                object.visualizer = new Mesh(new SphereGeometry(1, 16, 16), new MeshBasicMaterial({ color: 0xff0000 }));
                object.visualizer.position.set(0, 0.05, 0.05);
                sourceNode.add(object.visualizer);
            }
        }
        this.tracked.set(name, object);
    }
    getTrackedObjects() {
        return this.tracked;
    }
    removeObject(name) {
        this.tracked.forEach((o) => {
            if (o.name === name && o.positionalAudio !== undefined && o.positionalAudio.parent !== null)
                mainScene.remove(o.positionalAudio.parent);
        });
        this.tracked.delete(name);
    }
    setThreshold(name, threshold) {
        const obj = this.tracked.get(name);
        if (obj !== undefined)
            obj.threshold = threshold;
    }
    getThreshold(name) {
        var _a;
        return (_a = this.tracked.get(name)) === null || _a === void 0 ? void 0 : _a.threshold;
    }
    clearViolations(name) {
        if (name !== undefined) {
            const obj = this.tracked.get(name);
            if (obj !== undefined)
                obj.violations = [];
        }
        else {
            this.tracked.forEach((o) => (o.violations = []));
        }
    }
    getAllStats() {
        const out = new Map();
        this.tracked.forEach((object, name) => {
            const stats = object.getStats();
            stats.stageChanges = this.stageChanges.map(change => (Object.assign({}, change)));
            out.set(name, stats);
        });
        return out;
    }
    getSpeedHistoryCSV(sep = ",") {
        let out = ["object", "stage", "timestamp", "speed", "violation"].join(sep) + "\n";
        this.tracked.forEach((object, name) => {
            object.history.forEach(sample => {
                var _a;
                out += [name, (_a = sample.stage) !== null && _a !== void 0 ? _a : "unknown", sample.timestamp, sample.speed, sample.isViolation].join(sep) + "\n";
            });
        });
        return out;
    }
    deactivateTrackedObjects() {
        for (const object of this.tracked.values()) {
            object.deactivate();
        }
    }
}
